import { useCallback, useEffect, useRef } from "react";
import {
  ContentState,
  RawDraftContentBlock,
  RawDraftContentState,
  RawDraftEntity,
  RawDraftEntityRange,
} from "draft-js";
import { criarArquivo } from "helpers";
import runWhenIdle from "helpers/runWhenIdle";
import { canPersistDrafts } from "helpers/clearDrafts";

// Acima disso a imagem não é persistida como rascunho: o localStorage da
// origem costuma ter só ~5-10MB, e o base64 infla o arquivo em ~33%.
const MAX_DRAFT_IMAGE_SIZE = 1.5 * 1024 * 1024;

// Espaço máximo (em caracteres, como o navegador conta) que o localStorage da
// origem pode ocupar somando as imagens de rascunho. A cota costuma ser ~5M
// caracteres para a origem inteira: sem esse teto, poucas imagens a
// esgotam e o rascunho de TEXTO (e o resto da aplicação) não consegue mais
// gravar nada.
const DRAFT_STORAGE_BUDGET_CHARS = 3 * 1024 * 1024;

/** Caracteres ocupados no localStorage, ignorando a chave informada. */
const localStorageUsage = (ignoreKey?: string) => {
  let total = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || key === ignoreKey) continue;
      total += key.length + (localStorage.getItem(key)?.length || 0);
    }
  } catch (error) {
    // Sem acesso ao storage: trata como cheio.
    return Infinity;
  }
  return total;
};

// Tempo máximo que uma imagem de rascunho fica guardada no localStorage.
// Depois disso ela é descartada (e removida do storage) mesmo que o rascunho
// de texto ainda exista, para não acumular imagens esquecidas no navegador.
export const DRAFT_IMAGE_TTL_MS = 24 * 60 * 60 * 1000;

interface PersistedImage {
  data: string;
  name: string;
  type: string;
  createdAt: number;
}

const isBlobUrl = (src?: string | null): src is string =>
  typeof src === "string" && src.startsWith("blob:");

/** Entidades de imagem local (blob:) de um conteúdo serializado do editor. */
const getLocalImageEntries = (raw: RawDraftContentState) =>
  Object.entries(raw.entityMap || {}).filter(
    ([, entity]: [string, RawDraftEntity]) =>
      entity.type === "IMAGE" && isBlobUrl(entity.data?.src)
  );

/**
 * Remove do conteúdo as entidades informadas, junto com os blocos atômicos
 * (imagem) que as exibem.
 */
const removeEntities = (
  raw: RawDraftContentState,
  entityKeys: Set<string>
): RawDraftContentState => {
  const blocks = raw.blocks
    .filter(
      (block: RawDraftContentBlock) =>
        !(
          block.type === "atomic" &&
          block.entityRanges?.some((range: RawDraftEntityRange) =>
            entityKeys.has(String(range.key))
          )
        )
    )
    .map((block: RawDraftContentBlock) => ({
      ...block,
      entityRanges: (block.entityRanges || []).filter(
        (range: RawDraftEntityRange) => !entityKeys.has(String(range.key))
      ),
    }));

  const entityMap = { ...raw.entityMap };
  entityKeys.forEach((key) => delete entityMap[key]);

  return { ...raw, blocks, entityMap };
};

/** Indica se o conteúdo tem alguma imagem local (blob:) pendente de upload. */
export const hasLocalImage = (content: ContentState) => {
  let found = false;
  content.getBlockMap().forEach((block) => {
    block!.findEntityRanges(
      (char) => char.getEntity() !== null,
      (start) => {
        if (found) return;
        const entity = content.getEntity(block!.getEntityAt(start));
        found =
          entity.getType() === "IMAGE" && isBlobUrl(entity.getData()?.src);
      }
    );
  });
  return found;
};

const isExpired = (stored: PersistedImage) =>
  Date.now() - stored.createdAt > DRAFT_IMAGE_TTL_MS;

/**
 * Mantém as imagens inseridas no editor como URLs locais (blob:) até o
 * momento do submit, quando então são de fato enviadas ao servidor.
 * Usado pelas telas que inserem imagem no corpo de um editor rich-text
 * (CustomImageModalLocal / handleImageUploadLocal) para evitar upload de
 * imagens que o usuário nunca chega a salvar.
 *
 * Quando `imagesStorageKey` é informado, as imagens pendentes também são
 * persistidas (em base64) no localStorage, para sobreviver a um refresh
 * junto com o rascunho de texto, por até `DRAFT_IMAGE_TTL_MS`.
 */
export const useLocalImageUpload = (
  imagesStorageKey?: string | null,
  onPersistError?: () => void
) => {
  const pendingImagesRef = useRef<Map<string, File>>(new Map());

  // Espelho em memória do que está no localStorage: é a fonte de verdade para
  // leitura/modificação. Como a gravação é adiada (runWhenIdle), reler o
  // storage a cada mudança perderia atualizações concorrentes (ex: duas
  // imagens inseridas em sequência).
  // O espelho guarda a chave a que pertence: callbacks assíncronos antigos
  // (FileReader, restauração, gravação agendada) de uma chave anterior nunca
  // podem ler/gravar o espelho de outra chave (ex: outro paciente).
  const persistedRef = useRef<{
    key: string;
    images: Record<string, PersistedImage>;
  } | null>(null);
  // Chave vigente (atualizada no render) para detectar closures antigas.
  const currentKeyRef = useRef(imagesStorageKey);
  currentKeyRef.current = imagesStorageKey;
  const flushScheduledRef = useRef(false);
  // Incrementado ao cancelar gravações pendentes (troca de chave / limpeza).
  const flushGenerationRef = useRef(0);
  const onPersistErrorRef = useRef(onPersistError);
  onPersistErrorRef.current = onPersistError;

  const isCurrentKey = useCallback(
    () => !!imagesStorageKey && imagesStorageKey === currentKeyRef.current,
    [imagesStorageKey]
  );

  // Fora da chave vigente devolve um objeto descartável, para que closures
  // antigas não contaminem o espelho da chave atual.
  const getPersistedImages = useCallback((): Record<string, PersistedImage> => {
    if (!imagesStorageKey || !isCurrentKey()) return {};
    if (persistedRef.current?.key !== imagesStorageKey) {
      let images: Record<string, PersistedImage> = {};
      try {
        images = JSON.parse(localStorage.getItem(imagesStorageKey) || "{}");
      } catch (error) {
        localStorage.removeItem(imagesStorageKey);
      }
      persistedRef.current = { key: imagesStorageKey, images };
    }
    return persistedRef.current.images;
  }, [imagesStorageKey, isCurrentKey]);

  const writeMirror = useCallback((storageKey: string) => {
    if (persistedRef.current?.key !== storageKey) return;
    if (!canPersistDrafts()) return;
    const { images } = persistedRef.current;
    try {
      if (Object.keys(images).length) {
        localStorage.setItem(storageKey, JSON.stringify(images));
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch (error) {
      // Cota do localStorage estourada: a imagem não sobrevive a um
      // refresh; avisa o chamador sem quebrar o restante do rascunho.
      onPersistErrorRef.current?.();
    }
  }, []);

  // Agenda (coalescida) a gravação do espelho no localStorage. setItem é
  // síncrono; adia pro navegador ficar ocioso pra não travar a thread
  // principal logo após o usuário inserir a imagem.
  const schedulePersist = useCallback(() => {
    if (!imagesStorageKey || !isCurrentKey() || flushScheduledRef.current)
      return;
    flushScheduledRef.current = true;
    const generation = flushGenerationRef.current;

    runWhenIdle(() => {
      if (generation !== flushGenerationRef.current) return;
      flushScheduledRef.current = false;
      writeMirror(imagesStorageKey);
    });
  }, [imagesStorageKey, isCurrentKey, writeMirror]);

  const cancelPendingPersist = useCallback(() => {
    flushGenerationRef.current += 1;
    flushScheduledRef.current = false;
  }, []);

  // Ao trocar de chave (ex: outro paciente), desmontar ou fechar a aba, grava
  // de imediato o que ainda estava agendado (em vez de descartar). O espelho
  // da chave anterior é trocado sob demanda em getPersistedImages.
  useEffect(() => {
    if (!imagesStorageKey) return;

    const flushNow = () => {
      if (!flushScheduledRef.current) return;
      cancelPendingPersist();
      writeMirror(imagesStorageKey);
    };

    window.addEventListener("pagehide", flushNow);
    return () => {
      window.removeEventListener("pagehide", flushNow);
      flushNow();
    };
  }, [imagesStorageKey, cancelPendingPersist, writeMirror]);

  // Remove do localStorage as imagens de rascunho com mais de 24h, mesmo
  // quando não há restauração de texto acontecendo (ex: imagem inserida
  // durante uma edição de post existente, que nunca vira rascunho de
  // texto). Evita deixar imagens esquecidas ocupando espaço indefinidamente.
  const pruneExpiredImages = useCallback(() => {
    if (!imagesStorageKey) return;
    const images = getPersistedImages();
    let changed = false;

    Object.entries(images).forEach(([blobUrl, stored]) => {
      if (isExpired(stored)) {
        delete images[blobUrl];
        changed = true;
      }
    });

    if (changed) schedulePersist();
  }, [imagesStorageKey, getPersistedImages, schedulePersist]);

  const onLocalFileSelected = useCallback(
    (blobUrl: string, file: File) => {
      pendingImagesRef.current.set(blobUrl, file);

      if (!imagesStorageKey || file.size > MAX_DRAFT_IMAGE_SIZE) return;

      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result !== "string") return;
        // O blob pode ter sido descartado/enviado enquanto lia o arquivo.
        if (!pendingImagesRef.current.has(blobUrl)) return;

        // Sem espaço para a imagem: ela segue no post (é enviada no submit),
        // só não sobrevive a um refresh. O chamador avisa o usuário.
        const images = getPersistedImages();
        const mirrorChars = Object.values(images).reduce(
          (sum, stored) => sum + stored.data.length + stored.name.length + 100,
          0
        );
        const projected =
          localStorageUsage(imagesStorageKey) +
          mirrorChars +
          reader.result.length;
        if (projected > DRAFT_STORAGE_BUDGET_CHARS) {
          onPersistErrorRef.current?.();
          return;
        }

        images[blobUrl] = {
          data: reader.result,
          name: file.name,
          type: file.type,
          createdAt: Date.now(),
        };
        schedulePersist();
      };
      reader.readAsDataURL(file);
    },
    [imagesStorageKey, getPersistedImages, schedulePersist]
  );

  // Retorna os blob: enviados. As cópias deles no rascunho NÃO são removidas
  // aqui: se o salvamento do post falhar depois do upload, o rascunho de
  // texto ainda aponta para esses blob: e precisa delas para sobreviver a um
  // refresh. O chamador remove com `removePersistedImages` após salvar.
  const uploadPendingImages = useCallback(
    async (rawContent: RawDraftContentState) => {
      const entries = getLocalImageEntries(rawContent);
      const uploadedBlobUrls: string[] = [];

      for (const [key, entity] of entries) {
        const blobUrl = entity.data.src;
        const file = pendingImagesRef.current.get(blobUrl);
        if (!file) {
          throw new Error(
            "Não foi possível localizar o arquivo local da imagem inserida."
          );
        }

        const {
          data: {
            arquivos: [uploadedFile],
          },
        } = await criarArquivo([file], "1", "", true);

        rawContent.entityMap[key].data.src = uploadedFile.path;
        URL.revokeObjectURL(blobUrl);
        pendingImagesRef.current.delete(blobUrl);
        uploadedBlobUrls.push(blobUrl);
      }

      return uploadedBlobUrls;
    },
    []
  );

  const removePersistedImages = useCallback(
    (blobUrls: string[]) => {
      const images = getPersistedImages();
      let changed = false;
      blobUrls.forEach((blobUrl) => {
        if (images[blobUrl]) {
          delete images[blobUrl];
          changed = true;
        }
      });
      if (changed) schedulePersist();
    },
    [getPersistedImages, schedulePersist]
  );

  const discardPendingImages = useCallback(() => {
    pendingImagesRef.current.forEach((_file, blobUrl) => {
      URL.revokeObjectURL(blobUrl);
    });
    pendingImagesRef.current.clear();
  }, []);

  // Limpeza síncrona: cancela gravações pendentes para que nenhuma delas
  // recrie a chave depois.
  const clearPersistedImages = useCallback(() => {
    if (!imagesStorageKey) return;
    cancelPendingPersist();
    persistedRef.current = { key: imagesStorageKey, images: {} };
    localStorage.removeItem(imagesStorageKey);
  }, [imagesStorageKey, cancelPendingPersist]);

  // Reconstrói, a partir do localStorage, as imagens locais (blob:) de um
  // rascunho restaurado após um refresh. Os blobs antigos já morreram junto
  // com a página, então cada imagem recuperável ganha um novo blob: local.
  // Entidades sem correspondência salva (acima do limite de tamanho, cota
  // estourada, mais de 24h) são removidas do conteúdo, e o retorno sinaliza
  // isso para o chamador avisar o usuário.
  //
  // A restauração é em duas fases: nada é alterado (pendências nem storage)
  // até o chamador decidir aplicar o rascunho. Ele deve chamar `commit()` ao
  // aplicar — e então regravar o rascunho de TEXTO com o `raw` retornado, já
  // que as imagens são re-chaveadas para os novos blob: — ou `discard()` se
  // desistir (ex: trocou de paciente ou o usuário digitou no meio tempo),
  // mantendo o storage intacto para uma próxima restauração.
  const restoreDraftImages = useCallback(
    async (raw: RawDraftContentState) => {
      const noop = () => {};
      const blobEntries = getLocalImageEntries(raw);

      if (!blobEntries.length) {
        return { raw, hadUnresolvedImages: false, commit: noop, discard: noop };
      }

      // Cópia rasa: as leituras não dependem do espelho durante os awaits.
      const persistedImages = { ...getPersistedImages() };
      const unresolvedEntityKeys = new Set<string>();
      const expiredBlobUrls: string[] = [];
      const recreated: {
        oldBlobUrl: string;
        newBlobUrl: string;
        file: File;
        stored: PersistedImage;
      }[] = [];

      for (const [entityKey, entity] of blobEntries) {
        const oldBlobUrl = entity.data.src as string;

        // Blob ainda vivo nesta sessão (ex: rascunho reaplicado após editar
        // um post, sem refresh): já está resolvido, não precisa recriar.
        if (pendingImagesRef.current.has(oldBlobUrl)) continue;

        const stored = persistedImages[oldBlobUrl];

        if (!stored || isExpired(stored)) {
          unresolvedEntityKeys.add(entityKey);
          if (stored) expiredBlobUrls.push(oldBlobUrl);
          continue;
        }

        try {
          const blob = await (await fetch(stored.data)).blob();
          const file = new File([blob], stored.name, { type: stored.type });
          const newBlobUrl = URL.createObjectURL(file);

          raw.entityMap[entityKey].data.src = newBlobUrl;
          recreated.push({ oldBlobUrl, newBlobUrl, file, stored });
        } catch (error) {
          unresolvedEntityKeys.add(entityKey);
        }
      }

      const discard = () => {
        recreated.forEach(({ newBlobUrl }) => URL.revokeObjectURL(newBlobUrl));
      };

      const commit = () => {
        // A chave mudou durante a restauração: não mexe no storage de outra.
        if (!isCurrentKey()) {
          discard();
          return;
        }
        const images = getPersistedImages();
        expiredBlobUrls.forEach((blobUrl) => delete images[blobUrl]);
        recreated.forEach(({ oldBlobUrl, newBlobUrl, file, stored }) => {
          pendingImagesRef.current.set(newBlobUrl, file);
          delete images[oldBlobUrl];
          images[newBlobUrl] = stored;
        });
        if (expiredBlobUrls.length || recreated.length) schedulePersist();
      };

      if (!unresolvedEntityKeys.size) {
        return { raw, hadUnresolvedImages: false, commit, discard };
      }

      return {
        raw: removeEntities(raw, unresolvedEntityKeys),
        hadUnresolvedImages: true,
        commit,
        discard,
      };
    },
    [getPersistedImages, isCurrentKey, schedulePersist]
  );

  useEffect(() => {
    return () => discardPendingImages();
  }, [discardPendingImages]);

  return {
    onLocalFileSelected,
    uploadPendingImages,
    discardPendingImages,
    clearPersistedImages,
    removePersistedImages,
    pruneExpiredImages,
    restoreDraftImages,
  };
};
